import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

const ForgotPasswordForm = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast({
        title: "Error",
        description: "Please enter your email address.",
        variant: "destructive",
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await apiRequest("POST", "/api/forgot-password", { email });
      
      if (response.ok) {
        setIsSubmitted(true);
        toast({
          title: "Success",
          description: "If an account with that email exists, you'll receive password reset instructions.",
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast({
          title: "Error",
          description: errorData.message || "An unexpected error occurred. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Forgot password request failed:", error);
      toast({
        title: "Error",
        description: "Failed to submit request. Please check your connection and try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="space-y-4 text-center">
        <h3 className="text-lg font-medium !text-gray-900 dark:!text-gray-900">Check Your Email</h3>
        <p className="text-sm text-gray-500 dark:text-gray-500">
          If an account with that email exists, we've sent password reset instructions.
          Please check your email inbox and spam folder.
        </p>
        <Button 
          className="w-full !bg-white dark:!bg-white !text-gray-900 dark:!text-gray-900 !border-gray-200 dark:!border-gray-200 hover:!bg-gray-50 dark:hover:!bg-gray-50" 
          variant="outline"
          onClick={() => setIsSubmitted(false)}
        >
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="email" className="!text-gray-900 dark:!text-gray-900">Email Address</Label>
        <Input
          id="email"
          name="email"
          type="email"
          placeholder="Enter your email address"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="!bg-white dark:!bg-white !border-gray-200 dark:!border-gray-200 !text-gray-900 dark:!text-gray-900 placeholder:!text-gray-500 dark:placeholder:!text-gray-500 focus:!ring-blue-500 dark:focus:!ring-blue-500 focus:!border-blue-500 dark:focus:!border-blue-500"
        />
      </div>
      
      <Button 
        type="submit" 
        className="w-full !bg-blue-600 hover:!bg-blue-700 !text-white dark:!bg-blue-600 dark:hover:!bg-blue-700 dark:!text-white py-6 !border-0 dark:!border-0"
        disabled={isSubmitting}
      >
        {isSubmitting ? "Sending..." : "Send Reset Link"}
      </Button>
    </form>
  );
};

export default ForgotPasswordForm; 